import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { X, ShieldCheck, Ticket, Users, Mail, ShieldAlert, Key } from 'lucide-react';
import toast from 'react-hot-toast';
import { useClients } from '../../hooks/useClients';
import {
  useCreateReportDefinition,
  useUpdateReportDefinition,
  useReportDefinition,
} from '../../hooks/useScheduledReports';
import {
  ReportType,
  ScheduleFrequency,
  REPORT_TYPE_INFO,
  DAYS_OF_WEEK,
  CreateReportDefinitionRequest,
} from '../../types';

interface ReportDefinitionFormProps {
  definitionId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const REPORT_TYPES: { type: ReportType; icon: typeof ShieldCheck }[] = [
  { type: 'mfa', icon: ShieldCheck },
  { type: 'license', icon: Ticket },
  { type: 'admin-roles', icon: Users },
  { type: 'mailbox-security', icon: Mail },
  { type: 'email-security', icon: ShieldAlert },
  { type: 'conditional-access', icon: Key },
];

const TIME_OPTIONS = [
  '00:00', '01:00', '02:00', '03:00', '04:00', '05:00',
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
  '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
  '18:00', '19:00', '20:00', '21:00', '22:00', '23:00',
];

export default function ReportDefinitionForm({
  definitionId,
  onClose,
  onSuccess,
}: ReportDefinitionFormProps) {
  const isEditing = !!definitionId;

  // Form state
  const [clientId, setClientId] = useState('');
  const [reportType, setReportType] = useState<ReportType>('mfa');
  const [frequency, setFrequency] = useState<ScheduleFrequency>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(1); // Monday
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [time, setTime] = useState('06:00');
  const [enabled, setEnabled] = useState(true);
  const [uploadToITGlue, setUploadToITGlue] = useState(false);
  const [emailOnComplete, setEmailOnComplete] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');

  // Hooks
  const { clients, loading: clientsLoading } = useClients({ pageSize: 500 });
  const { definition, loading: loadingDefinition } = useReportDefinition(definitionId || null);
  const { createDefinition, loading: creating } = useCreateReportDefinition();
  const { updateDefinition, loading: updating } = useUpdateReportDefinition();

  // Load existing definition for editing
  useEffect(() => {
    if (definition) {
      setClientId(definition.clientId);
      setReportType(definition.reportType);
      setFrequency(definition.schedule.frequency);
      setDayOfWeek(definition.schedule.dayOfWeek ?? 1);
      setDayOfMonth(definition.schedule.dayOfMonth ?? 1);
      setTime(definition.schedule.time ?? '06:00');
      setEnabled(definition.schedule.enabled);
      setUploadToITGlue(definition.options.uploadToITGlue);
      setEmailOnComplete(definition.options.emailOnComplete);
      setEmailRecipients(definition.options.emailRecipients?.join(', ') ?? '');
    }
  }, [definition]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientId) {
      toast.error('Please select a client');
      return;
    }

    const scheduleData = {
      enabled,
      frequency,
      ...(frequency === 'weekly' && { dayOfWeek }),
      ...(frequency === 'monthly' && { dayOfMonth }),
      ...(frequency !== 'manual' && { time }),
    };

    const optionsData = {
      uploadToITGlue,
      emailOnComplete,
      ...(emailOnComplete && emailRecipients && {
        emailRecipients: emailRecipients.split(',').map(e => e.trim()).filter(Boolean)
      }),
    };

    try {
      if (isEditing && definitionId) {
        await updateDefinition({
          id: definitionId,
          data: {
            schedule: scheduleData,
            options: optionsData,
          },
        });
        toast.success('Report definition updated');
      } else {
        const request: CreateReportDefinitionRequest = {
          clientId,
          reportType,
          schedule: scheduleData,
          options: optionsData,
        };
        await createDefinition(request);
        toast.success('Report definition created');
      }
      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const loading = creating || updating || loadingDefinition;

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 [&>button]:hidden">
        <div className="px-4 pb-4 pt-5 sm:p-6">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
              className="rounded-md text-muted-foreground hover:text-foreground"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <DialogTitle
            className="text-lg font-semibold text-foreground mb-6"
          >
            {isEditing ? 'Edit Scheduled Report' : 'Create Scheduled Report'}
          </DialogTitle>
          <DialogDescription className="sr-only">Configure report type, schedule, and delivery options for a scheduled report</DialogDescription>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Client Selection */}
            {!isEditing && (
              <div>
                <label className="label">Client</label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="input"
                  disabled={clientsLoading}
                  required
                >
                  <option value="">Select a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.companyName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Report Type Selection */}
            {!isEditing && (
              <div>
                <label className="label">Report Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {REPORT_TYPES.map(({ type, icon: Icon }) => {
                    const info = REPORT_TYPE_INFO[type];
                    const checked = reportType === type;
                    return (
                      <div
                        key={type}
                        onClick={() => setReportType(type)}
                        className={`relative flex cursor-pointer rounded-lg border p-4 transition-colors ${
                          checked
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-border hover:border-border'
                        }`}
                        role="radio"
                        aria-checked={checked}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            setReportType(type);
                          }
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <Icon
                            className={`h-5 w-5 ${
                              checked
                                ? 'text-primary-600 dark:text-primary-400'
                                : 'text-muted-foreground'
                            }`}
                          />
                          <div>
                            <p
                              className={`font-medium ${
                                checked
                                  ? 'text-primary-900 dark:text-primary-100'
                                  : 'text-foreground'
                              }`}
                            >
                              {info.name}
                            </p>
                            <p
                              className="text-xs text-muted-foreground mt-1"
                            >
                              {info.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Schedule Section */}
            <div className="border-t border-border pt-6">
              <h4 className="font-medium text-foreground mb-4">Schedule</h4>

              <div className="grid grid-cols-2 gap-4">
                {/* Frequency */}
                <div>
                  <label className="label">Frequency</label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value as ScheduleFrequency)}
                    className="input"
                  >
                    <option value="manual">Manual only</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                {/* Day of Week (for weekly) */}
                {frequency === 'weekly' && (
                  <div>
                    <label className="label">Day of Week</label>
                    <select
                      value={dayOfWeek}
                      onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
                      className="input"
                    >
                      {DAYS_OF_WEEK.map((day, idx) => (
                        <option key={idx} value={idx}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Day of Month (for monthly) */}
                {frequency === 'monthly' && (
                  <div>
                    <label className="label">Day of Month</label>
                    <select
                      value={dayOfMonth}
                      onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
                      className="input"
                    >
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Time (for non-manual) */}
                {frequency !== 'manual' && (
                  <div>
                    <label className="label">Time (UTC)</label>
                    <select
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="input"
                    >
                      {TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Enable/Disable */}
              <div className="mt-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    className="rounded border-border text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-foreground">
                    Enable scheduled runs
                  </span>
                </label>
              </div>
            </div>

            {/* Options Section */}
            <div className="border-t border-border pt-6">
              <h4 className="font-medium text-foreground mb-4">Options</h4>

              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={uploadToITGlue}
                    onChange={(e) => setUploadToITGlue(e.target.checked)}
                    className="rounded border-border text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-foreground">
                    Upload to ITGlue
                  </span>
                  <span className="text-xs text-muted-foreground">(coming soon)</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={emailOnComplete}
                    onChange={(e) => setEmailOnComplete(e.target.checked)}
                    className="rounded border-border text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-foreground">
                    Email when complete
                  </span>
                  <span className="text-xs text-muted-foreground">(coming soon)</span>
                </label>

                {emailOnComplete && (
                  <div className="ml-6">
                    <label className="label text-sm">Recipients (comma-separated)</label>
                    <input
                      type="text"
                      value={emailRecipients}
                      onChange={(e) => setEmailRecipients(e.target.value)}
                      className="input"
                      placeholder="user@example.com, another@example.com"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Report'}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
